import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Loader2, FileEdit, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { VAT_TYPES } from "@/lib/translations";
import { addDays, r2, today, VAT_RATE, fmt } from "@/lib/format";
import { Field, Money, NativeSelect, PageHeader, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const LINE = { description: "", quantity: 1, unit_price: 0, vat_type: "standard" };
export const CURRENCIES = ["AED", "USD", "EUR"];

function LineRow({ line, idx, onChange, onRemove, canRemove, cur }) {
  const { t } = useLang();
  const net = r2(line.quantity * line.unit_price);
  const set = (k, num) => (e) => onChange({ ...line, [k]: num ? Number(e.target.value) : e.target.value });
  return (
    <div className="grid grid-cols-12 items-end gap-3 border-b border-slate-100 py-3" data-testid={`invoice-line-${idx}`}>
      <Field label={t("description")} className="col-span-12 md:col-span-4"><Input data-testid={`line-desc-${idx}`} value={line.description} onChange={set("description")} required /></Field>
      <Field label={t("quantity")} className="col-span-3 md:col-span-1"><Input data-testid={`line-qty-${idx}`} type="number" min="0.01" step="0.01" className="font-mono" value={line.quantity} onChange={set("quantity", true)} /></Field>
      <Field label={`${t("unitPrice")} (${cur})`} className="col-span-5 md:col-span-2"><Input data-testid={`line-price-${idx}`} type="number" min="0" step="0.01" className="font-mono" value={line.unit_price} onChange={set("unit_price", true)} /></Field>
      <Field label={t("vatType")} className="col-span-4 md:col-span-2">
        <NativeSelect data-testid={`line-vat-${idx}`} value={line.vat_type} onChange={set("vat_type")}>
          {VAT_TYPES.map((v) => <option key={v} value={v}>{t(`vat_${v}`)}</option>)}
        </NativeSelect>
      </Field>
      <div className="col-span-10 pb-2 text-end md:col-span-2"><Money value={net} cur={cur} className="text-sm" /></div>
      <div className="col-span-2 pb-1 text-end md:col-span-1">
        {canRemove && <button type="button" data-testid={`line-remove-${idx}`} onClick={onRemove} className="rounded p-2 text-slate-400 hover:bg-red-50 hover:text-[#CE1126]"><Trash2 className="h-4 w-4" /></button>}
      </div>
    </div>
  );
}

function CurrencyFields({ f, setF }) {
  const { t } = useLang();
  const [fx, setFx] = useState(null);
  const fetchRate = async (cur) => {
    if (cur === "AED") { setFx(null); setF((p) => ({ ...p, currency: "AED", fx_rate: 1 })); return; }
    try {
      const { data } = await api.get("/fx", { params: { currency: cur } });
      setFx(data);
      setF((p) => ({ ...p, currency: cur, fx_rate: data.rate }));
    } catch { setF((p) => ({ ...p, currency: cur })); toast.error(t("fxUnavailable")); }
  };
  return (
    <>
      <Field label={t("currency")}>
        <NativeSelect data-testid="invoice-currency-select" value={f.currency} onChange={(e) => fetchRate(e.target.value)}>
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </NativeSelect>
      </Field>
      {f.currency !== "AED" && (
        <Field label={`${t("fxRate")} (1 ${f.currency} = ? AED)`}>
          <div className="flex gap-2">
            <Input data-testid="invoice-fx-rate" type="number" min="0.0001" step="0.0001" className="font-mono" value={f.fx_rate} onChange={(e) => setF({ ...f, fx_rate: Number(e.target.value) })} required />
            <Button type="button" variant="outline" size="icon" data-testid="invoice-fx-refresh" title={t("fxRefresh")} onClick={() => fetchRate(f.currency)}><RefreshCw className="h-4 w-4" /></Button>
          </div>
          {fx && <p className="mt-1 text-[11px] text-slate-400">{t("fxRateHint")} {fx.date}</p>}
        </Field>
      )}
    </>
  );
}

export default function InvoiceNew({ kind = "invoice" }) {
  const isQuote = kind === "quote";
  const { id } = useParams();
  const editing = !!id;
  const { t } = useLang();
  const nav = useNavigate();
  const [clients, setClients] = useState(null);
  const [f, setF] = useState({ client_id: "", issue_date: today(), due_date: addDays(today(), 30), notes: "", currency: "AED", fx_rate: 1 });
  const [lines, setLines] = useState([{ ...LINE }]);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    if (editing) api.get(`/invoices/${id}`).then((r) => {
      const inv = r.data;
      if (inv.status !== "draft") { toast.error(t("onlyDraftEditable")); nav(`/invoices/${id}`); return; }
      setF({ client_id: inv.client_id, issue_date: inv.issue_date, due_date: inv.due_date, notes: inv.notes, currency: inv.currency || "AED", fx_rate: inv.fx_rate || 1 });
      setLines(inv.items.map((it) => ({ description: it.description, quantity: it.quantity, unit_price: it.unit_price, vat_type: it.vat_type })));
    });
  }, [id, editing, nav, t]);

  const cur = f.currency || "AED";
  const sub = r2(lines.reduce((s, l) => s + r2(l.quantity * l.unit_price), 0));
  const vat = r2(lines.reduce((s, l) => s + r2(r2(l.quantity * l.unit_price) * VAT_RATE[l.vat_type]), 0));
  const rate = cur === "AED" ? 1 : f.fx_rate || 0;
  const submit = async (draft) => {
    if (!f.client_id) return toast.error(t("selectClient"));
    setBusy(draft ? "draft" : "issue");
    try {
      const payload = { ...f, items: lines, draft };
      let data;
      if (editing) {
        ({ data } = await api.put(`/invoices/${id}`, payload));
        if (!draft) ({ data } = await api.patch(`/invoices/${id}/status`, { status: "unpaid" }));
        toast.success(t(draft ? "invoiceUpdated" : "invoiceIssued"));
      } else {
        ({ data } = await api.post(isQuote ? "/quotes" : "/invoices", payload));
        toast.success(t(isQuote ? "quoteCreated" : draft ? "draftSaved" : "invoiceCreated"));
      }
      nav(isQuote ? `/quotes/${data.id}` : `/invoices/${data.id}`);
    } catch (err) {
      if (errMsg(err) === "PLAN_INVOICES") toast.error(t("planInvoicesLocked"), { action: { label: t("upgrade"), onClick: () => nav("/settings") } });
      else toast.error(errMsg(err));
    }
    setBusy("");
  };

  if (clients && clients.length === 0)
    return (
      <div data-testid="invoice-new-page">
        <PageHeader title={t(isQuote ? "newQuote" : "newInvoice")} />
        <Panel className="p-8 text-slate-600">{t("noClientsHint")} <Link to="/clients" data-testid="goto-clients-link" className="font-semibold text-[#007A3D] hover:underline">{t("clients")} →</Link></Panel>
      </div>
    );

  const title = editing ? t("editInvoice") : t(isQuote ? "newQuote" : "newInvoice");
  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(false); }} data-testid="invoice-new-page">
      <PageHeader title={title} subtitle={t(isQuote ? "newQuoteSubtitle" : editing ? "editInvoiceSubtitle" : "newInvoiceSubtitle")} />
      <Panel className="grid grid-cols-1 gap-4 p-6 md:grid-cols-3">
        <Field label={t("client")}>
          <NativeSelect data-testid="invoice-client-select" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} required>
            <option value="">{t("selectClient")}</option>
            {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </NativeSelect>
        </Field>
        <Field label={t("issueDate")}><Input data-testid="invoice-issue-date" type="date" className="font-mono" value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value })} required /></Field>
        <Field label={t(isQuote ? "validUntil" : "dueDate")}><Input data-testid="invoice-due-date" type="date" className="font-mono" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} required /></Field>
        <CurrencyFields f={f} setF={setF} />
      </Panel>
      <Panel className="mt-6 p-6">
        <h3 className="font-display text-lg font-bold">{t("items")}</h3>
        {lines.map((l, i) => (
          <LineRow key={i} idx={i} line={l} cur={cur} canRemove={lines.length > 1}
            onChange={(nl) => setLines(lines.map((x, j) => (j === i ? nl : x)))}
            onRemove={() => setLines(lines.filter((_, j) => j !== i))} />
        ))}
        <Button type="button" variant="outline" data-testid="add-line-btn" className="mt-4" onClick={() => setLines([...lines, { ...LINE }])}><Plus className="me-1 h-4 w-4" />{t("addLine")}</Button>
      </Panel>
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <Field label={t("notes")}><textarea data-testid="invoice-notes" rows={4} className="w-full rounded-md border border-slate-300 bg-white p-3 text-sm" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <Panel className="space-y-3 p-6">
          <div className="flex justify-between text-sm text-slate-600"><span>{t("subtotal")}</span><Money testid="invoice-subtotal" value={sub} cur={cur} /></div>
          <div className="flex justify-between text-sm text-slate-600"><span>{t("vat")} 5%</span><Money testid="invoice-vat" value={vat} cur={cur} /></div>
          <div className="flex justify-between border-t border-slate-200 pt-3 text-lg font-bold"><span>{t("totalInclVat")}</span><Money testid="invoice-total" value={r2(sub + vat)} cur={cur} className="text-[#007A3D]" /></div>
          {cur !== "AED" && (
            <div data-testid="invoice-aed-equivalent" className="rounded-md bg-slate-50 p-3 text-xs text-slate-600">
              <div className="flex justify-between"><span>{t("aedEquivalent")}</span><span className="font-mono font-semibold">AED {fmt(r2((sub + vat) * rate))}</span></div>
              <div className="flex justify-between"><span>{t("vat")} (AED)</span><span className="font-mono">AED {fmt(r2(vat * rate))}</span></div>
            </div>
          )}
          <div className="flex flex-col gap-2 pt-2 sm:flex-row">
            {!isQuote && (
              <Button type="button" variant="outline" data-testid="invoice-save-draft-btn" disabled={!!busy} onClick={() => submit(true)} className="h-11 flex-1">
                {busy === "draft" ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <FileEdit className="me-2 h-4 w-4" />}{t(editing ? "saveChanges" : "saveDraft")}
              </Button>
            )}
            <Button data-testid="invoice-submit-btn" disabled={!!busy} className="h-11 flex-1 bg-[#007A3D] hover:bg-[#005E2E]">
              {busy === "issue" && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t(isQuote ? "createQuote" : editing ? "issueInvoice" : "createInvoice")}
            </Button>
          </div>
        </Panel>
      </div>
    </form>
  );
}
